import React, { useMemo, useState } from 'react';
import { useObservable } from 'react-use-observable';

import { Button, Modal, Spin, Typography } from 'antd';

import { affiliateCommissionsService } from 'v2/services/reports/affiliateCommissions';

import { Icon } from '@eduzz/ui-antd-theme';

import { Autocomplete } from './Autocomplete';
import type { IAutocompleteOption } from './Autocomplete';
import { Table } from './Table';

interface IProps {
  isOpen: boolean;
  handleClose: () => void;
}

export const DetailsModal = ({ isOpen, handleClose }: IProps) => {
  const [selectedOptions, setSelectedOptions] = useState<IAutocompleteOption[]>([]);

  const [details] = useObservable(() => affiliateCommissionsService.getAffiliateCommissionsDetails(), []);
  const [loadingPartnerCommissionsDetails] = useObservable(
    () => affiliateCommissionsService.getLoadingAffiliateCommissionsDetails(),
    []
  );

  const handleAutoCompleteOnChange = (event: React.ChangeEvent<Element>, value: IAutocompleteOption[]) => {
    setSelectedOptions(value);
  };

  const autocompleteOptions: IAutocompleteOption[] = useMemo(() => {
    return details?.commissions?.map(commission => {
      const { product } = commission;

      return {
        value: product.id,
        label: `${product.id} - ${product.title}`
      };
    });
  }, [details]);

  const filteredCommissions = useMemo(() => {
    const selectedOptionsProductIds = selectedOptions.map(option => option.value);

    if (selectedOptionsProductIds.length) {
      return details?.commissions?.filter(commission => {
        const { product } = commission;

        return selectedOptionsProductIds.includes(product.id);
      });
    }

    return details?.commissions;
  }, [details, selectedOptions]);

  const handleDialogClose = () => {
    handleClose();
    setSelectedOptions([]);
  };

  return (
    <Modal
      open={isOpen}
      onCancel={handleDialogClose}
      width={670}
      title='Mais detalhes'
      footer={
        <div className='flex justify-end'>
          <Button type='text' onClick={handleDialogClose}>
            Fechar
          </Button>
        </div>
      }
    >
      {loadingPartnerCommissionsDetails && (
        <div className='flex items-center justify-center p-8'>
          <Spin size='large' />
        </div>
      )}
      {!loadingPartnerCommissionsDetails && (
        <div className='flex max-h-130 flex-col gap-4 overflow-y-auto'>
          <div className='rounded-(--uizz-antd-border-radius-lg) bg-(--uizz-antd-color-fill-tertiary) p-3'>
            <div className='flex items-center gap-2'>
              <Icon.User />
              <Typography.Text strong>
                Afiliado: <Typography.Text type='secondary'>{details?.commissioned_name}</Typography.Text>
              </Typography.Text>
            </div>
          </div>

          {details?.commissions?.length > 3 && (
            <Autocomplete
              options={autocompleteOptions}
              selectedOptions={selectedOptions}
              onChange={handleAutoCompleteOnChange}
            />
          )}

          <Table commissions={filteredCommissions} />
        </div>
      )}
    </Modal>
  );
};
